/**
 * Elite Batch 23 Part 1 — Sales & Lead Management Workflows (1-18)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "HubSpot: Fetch contact profile", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 1 — Sales & Lead Management Workflows (1-18)
// =============================================================================

export const ELITE_BATCH23_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1. Web Form Lead to CRM + Email Nurture — Pattern K
  // ===================================================================
  "Web Form Lead to CRM + Email Nurture": {
    nodes: [
      trigger("Web Form: New Lead Submission", "webhook_form", {
        source: "WebForm",
        event: "form.submitted",
        howItStarts:
          "Fires when a prospect submits a lead capture form on your website (contact us, demo request, pricing inquiry)",
        setup:
          "Configure your website form to POST submissions to this webhook URL. Include fields for name, email, company, phone, and any qualifying questions.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract lead details and infer intent",
        280,
        "You are a sales lead qualification specialist. Parse the incoming form submission and extract structured lead data. Infer purchase intent and urgency from the form fields and any free-text responses.",
        "Extract from this web form submission: {{{1.body}}}. Return JSON with: lead_email, lead_name, company_name, phone, job_title, message_text, inferred_intent (demo/pricing/info/partnership), urgency_level (high/medium/low).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "inferred_intent", as: "inferred_intent", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Deduplicate and create or update contact",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
              },
            ],
            variables: [
              {
                name: "total",
                customName: "existing_contact_count",
                type: "number",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "hubspot_contact_id",
                type: "string",
              },
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "hitApi", "Clearbit: Enrich lead with company data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
          ],
        },
      }),

      n("5", "runCode", "Compute lead score from enrichment + intent", 1000, {
        runCodeDetails: {
          code: `const intentScore = urgency_level === 'high' ? 40 : urgency_level === 'medium' ? 25 : 10;
const sizeScore = employee_count > 500 ? 30 : employee_count > 50 ? 20 : 10;
const revenueScore = annual_revenue > 10000000 ? 20 : annual_revenue > 1000000 ? 15 : 5;
const totalScore = intentScore + sizeScore + revenueScore;
return { lead_score: totalScore, score_tier: totalScore > 60 ? 'hot' : totalScore > 40 ? 'warm' : 'cold' };`,
          variables: [
            { name: "lead_score", customName: "lead_score", type: "number" },
            { name: "score_tier", customName: "score_tier", type: "string" },
          ],
        },
      }),

      n(
        "6",
        "condition",
        "Is lead score above qualification threshold?",
        1240,
        {
          conditionDetails: {
            conditions: [
              { field: "lead_score", operator: "greaterThan", value: "60" },
            ],
          },
        },
      ),

      emailNode(
        "7",
        "SendGrid: Enroll lead in nurture sequence",
        1480,
        "{lead_email}",
        "Thanks for reaching out, {lead_name} — here's what happens next",
        "Hi {lead_name},\n\nThank you for your interest! Based on your inquiry about {inferred_intent}, we've put together some resources tailored to {enriched_company} in the {industry} space.\n\nHere's what you can expect:\n1. A personalized demo walkthrough (if requested)\n2. Case studies from companies similar to yours ({employee_count}+ employees)\n3. ROI calculator specific to your industry\n\nYour dedicated rep will be reaching out within 24 hours.\n\nBest regards,\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log qualified lead and score",
        1720,
        "Leads",
        `[["{lead_name}","{lead_email}","{company_name}","{lead_score}","{score_tier}","{inferred_intent}","{industry}","{employee_count}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Captures web form leads, extracts details via AI, deduplicates in HubSpot, enriches with Clearbit company data, computes a lead score from intent and firmographics, qualifies leads above threshold for nurture enrollment, and logs all leads to Google Sheets for pipeline tracking.",
  },

  // ===================================================================
  // 2. Facebook Lead Ads to CRM + Slack Alert — Pattern K
  // ===================================================================
  "Facebook Lead Ads to CRM + Slack Alert": {
    nodes: [
      trigger("Facebook: New Lead Ad Submission", "webhook_app", {
        source: "Facebook",
        event: "leadgen",
        howItStarts:
          "Fires when a prospect submits a Facebook Lead Ad form, capturing contact info directly from the ad unit without leaving Facebook",
        setup:
          "In Facebook Business Manager > Events Manager, configure a webhook for Lead Ad submissions. Connect your Facebook Page and Ad Account. Map form fields to the webhook payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract Facebook lead data and qualify",
        280,
        "You are a Facebook advertising lead processor. Parse the Facebook Lead Ad webhook payload and extract structured contact information. Assess initial lead quality based on available data.",
        "Extract from this Facebook Lead Ad webhook: {{{1.body}}}. Return JSON with: lead_email, lead_name, phone_number, company_name, ad_name, campaign_name, lead_source (facebook_lead_ad), company_size_estimate (enterprise/mid-market/smb based on any available signals).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "ad_name", as: "ad_name", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          {
            field: "company_size_estimate",
            as: "company_size_estimate",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Search for existing contact or create new",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{lead_email}","firstname":"{lead_name}","company":"{company_name}","phone":"{phone_number}","lead_source":"facebook_lead_ad","hs_lead_status":"NEW"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "hitApi", "Clearbit: Enrich company details from domain", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "category.sector", customName: "sector", type: "string" },
          ],
        },
      }),

      n("5", "condition", "Is this an enterprise-level lead?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "employee_count", operator: "greaterThan", value: "500" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert sales rep with lead context",
        1240,
        ":fire: *New Facebook Lead Ad Submission*\n\n*Name:* {lead_name}\n*Email:* {lead_email}\n*Company:* {enriched_company} ({industry})\n*Employees:* {employee_count}\n*Campaign:* {campaign_name}\n*Ad:* {ad_name}\n*Segment:* {company_size_estimate}\n\nHubSpot Contact: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      emailNode(
        "7",
        "SendGrid: Confirm submission and set expectations",
        1480,
        "{lead_email}",
        "Thanks for your interest, {lead_name}!",
        "Hi {lead_name},\n\nThank you for responding to our ad! We received your information and a member of our team will be in touch shortly.\n\nIn the meantime, here are some resources that might be helpful:\n- Product overview and demo video\n- Customer success stories in {industry}\n- Getting started guide\n\nWe look forward to speaking with you!\n\nBest,\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log Facebook lead for attribution",
        1720,
        "FB_Leads",
        `[["{lead_name}","{lead_email}","{company_name}","{employee_count}","{campaign_name}","{ad_name}","{company_size_estimate}","{industry}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures Facebook Lead Ad submissions, extracts contact data via AI, creates or updates a HubSpot contact, enriches with Clearbit firmographics, routes enterprise leads to Slack with full context, sends a confirmation email to the lead, and logs all leads to Google Sheets for campaign attribution tracking.",
  },

  // ===================================================================
  // 3. LinkedIn Lead Form to CRM + Enrichment — Pattern K
  // ===================================================================
  "LinkedIn Lead Form to CRM + Enrichment": {
    nodes: [
      trigger("LinkedIn: Lead Gen Form Submission", "webhook_app", {
        source: "LinkedIn",
        event: "lead_gen_form.submitted",
        howItStarts:
          "Fires when a prospect submits a LinkedIn Lead Gen Form attached to a Sponsored Content or Message Ad campaign",
        setup:
          "In LinkedIn Campaign Manager > Account Assets > Lead Gen Forms, configure webhook delivery. Ensure your LinkedIn partner integration forwards form submissions including name, email, company, job title, and seniority.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract LinkedIn lead and assess seniority",
        280,
        "You are a B2B lead qualification expert. Parse the LinkedIn Lead Gen Form payload and extract structured contact info. LinkedIn forms often include job title and company seniority which are high-value qualification signals.",
        "Extract from this LinkedIn Lead Gen Form webhook: {{{1.body}}}. Return JSON with: lead_email, lead_name, company_name, job_title, seniority_level (executive/director/manager/individual), linkedin_profile_url, campaign_name, form_name, is_decision_maker (boolean based on seniority).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "seniority_level", as: "seniority_level", type: "string" },
          {
            field: "linkedin_profile_url",
            as: "linkedin_profile_url",
            type: "string",
          },
          {
            field: "is_decision_maker",
            as: "is_decision_maker",
            type: "boolean",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create or update contact with LinkedIn data",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{lead_email}","firstname":"{lead_name}","company":"{company_name}","jobtitle":"{job_title}","lead_source":"linkedin_lead_gen","hs_lead_status":"NEW"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "hitApi", "Clearbit: Enrich with company firmographics", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      n(
        "5",
        "runCode",
        "Qualify lead: MQL scoring from seniority + firmographics",
        1000,
        {
          runCodeDetails: {
            code: `const seniorityScore = seniority_level === 'executive' ? 35 : seniority_level === 'director' ? 30 : seniority_level === 'manager' ? 20 : 10;
const companyScore = employee_count > 200 ? 25 : employee_count > 50 ? 15 : 5;
const revenueScore = annual_revenue > 10000000 ? 20 : annual_revenue > 1000000 ? 10 : 5;
const decisionMakerBonus = is_decision_maker ? 15 : 0;
const mqlScore = seniorityScore + companyScore + revenueScore + decisionMakerBonus;
return { mql_score: mqlScore, is_mql: mqlScore >= 65 };`,
            variables: [
              { name: "mql_score", customName: "mql_score", type: "number" },
              { name: "is_mql", customName: "is_mql", type: "boolean" },
            ],
          },
        },
      ),

      n("6", "condition", "Does lead qualify as MQL?", 1240, {
        conditionDetails: {
          conditions: [{ field: "is_mql", operator: "equals", value: "true" }],
        },
      }),

      slackNode(
        "7",
        "Slack: Alert SDR with qualified LinkedIn lead",
        1480,
        ":briefcase: *New MQL from LinkedIn Lead Gen*\n\n*Name:* {lead_name}\n*Title:* {job_title} ({seniority_level})\n*Company:* {enriched_company} ({industry})\n*Employees:* {employee_count} | Revenue: ${annual_revenue}\n*MQL Score:* {mql_score}/100\n*LinkedIn:* {linkedin_profile_url}\n*Decision Maker:* {is_decision_maker}\n\nHubSpot: https://app.hubspot.com/contacts/{hubspot_contact_id}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log LinkedIn lead with qualification data",
        1720,
        "LinkedIn_Leads",
        `[["{lead_name}","{lead_email}","{company_name}","{job_title}","{seniority_level}","{mql_score}","{is_mql}","{industry}","{employee_count}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Processes LinkedIn Lead Gen Form submissions, extracts contact data and seniority via AI, creates HubSpot contacts, enriches with Clearbit firmographics, computes an MQL score from seniority and company data, alerts SDRs via Slack for qualified leads, and logs all leads to Google Sheets with full qualification data.",
  },

  // ===================================================================
  // 4. Webinar Registrant to CRM + Follow-Up Sequence — Pattern I
  // ===================================================================
  "Webinar Registrant to CRM + Follow-Up Sequence": {
    nodes: [
      trigger("Zoom: Webinar Registration Received", "webhook_app", {
        source: "Zoom",
        event: "webinar.registration_created",
        howItStarts:
          "Fires when a new registrant signs up for a Zoom webinar, capturing their registration details and any custom questions answered",
        setup:
          "In Zoom Marketplace > App > Event Subscriptions, add a subscription for 'Webinar Registration Created'. Ensure the webhook includes registrant email, name, company, job title, and responses to custom registration questions.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract registrant details and tag interest",
        280,
        "You are a webinar registration processor. Parse the Zoom registration webhook and extract structured attendee data. Infer the registrants topic interest and buying stage from their title, company, and any custom question responses.",
        "Extract from this Zoom webinar registration webhook: {{{1.body}}}. Return JSON with: registrant_email, registrant_name, company_name, job_title, webinar_topic, webinar_date, custom_answers_summary, interest_area (product/thought-leadership/technical), buying_stage (research/evaluation/decision).",
        [
          { field: "registrant_email", as: "registrant_email", type: "string" },
          { field: "registrant_name", as: "registrant_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "webinar_topic", as: "webinar_topic", type: "string" },
          { field: "interest_area", as: "interest_area", type: "string" },
          { field: "buying_stage", as: "buying_stage", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Create contact and log webinar registration",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"email":"{registrant_email}","firstname":"{registrant_name}","company":"{company_name}","jobtitle":"{job_title}","lead_source":"webinar","latest_webinar":"{webinar_topic}","hs_lead_status":"NEW"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
              {
                name: "properties.hs_object_id",
                customName: "contact_id",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "runCode", "Tag source and determine nurture track", 760, {
        runCodeDetails: {
          code: `const nurtureTrack = buying_stage === 'decision' ? 'sales_fast_track' : buying_stage === 'evaluation' ? 'product_deep_dive' : 'educational_drip';
const sourceTag = 'webinar_' + webinar_topic.toLowerCase().replace(/\\s+/g, '_').substring(0, 30);
const priorityLevel = buying_stage === 'decision' ? 'high' : buying_stage === 'evaluation' ? 'medium' : 'standard';
return { nurture_track: nurtureTrack, source_tag: sourceTag, priority_level: priorityLevel };`,
          variables: [
            {
              name: "nurture_track",
              customName: "nurture_track",
              type: "string",
            },
            { name: "source_tag", customName: "source_tag", type: "string" },
            {
              name: "priority_level",
              customName: "priority_level",
              type: "string",
            },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Does registrant have existing open opportunity?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "priority_level", operator: "equals", value: "high" },
            ],
          },
        },
      ),

      emailNode(
        "6",
        "SendGrid: Confirm registration and send pre-webinar content",
        1240,
        "{registrant_email}",
        "You're registered for {webinar_topic}, {registrant_name}!",
        "Hi {registrant_name},\n\nYou're all set for our upcoming webinar: {webinar_topic}\n\nTo get the most out of the session, here are some prep resources:\n\n1. Pre-read: Key trends in {interest_area} for 2024\n2. Discussion guide with questions we'll cover\n3. Worksheet to follow along during the presentation\n\nWe'll also send you a reminder 1 hour before the event.\n\nSee you there!\nThe Events Team",
      ),

      slackNode(
        "7",
        "Slack: Notify sales team of high-priority registrant",
        1480,
        ":calendar: *High-Priority Webinar Registrant*\n\n*Name:* {registrant_name}\n*Company:* {company_name}\n*Title:* {job_title}\n*Webinar:* {webinar_topic}\n*Buying Stage:* {buying_stage}\n*Nurture Track:* {nurture_track}\n*Priority:* {priority_level}\n\nThis registrant appears to be in a {buying_stage} stage — consider reaching out before the event.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log webinar registration",
        1720,
        "Webinar_Registrants",
        `[["{registrant_name}","{registrant_email}","{company_name}","{webinar_topic}","{buying_stage}","{nurture_track}","{priority_level}","{interest_area}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Zoom webinar registrations, extracts attendee details and buying stage via AI, creates HubSpot contacts, determines the right nurture track based on buying stage, sends a confirmation email with prep materials, notifies sales via Slack for high-priority registrants, and logs all registrations to Google Sheets.",
  },

  // ===================================================================
  // 5. Demo Request Triage + AE Assignment — Pattern K
  // ===================================================================
  "Demo Request Triage + AE Assignment": {
    nodes: [
      trigger("Web Form: Demo Request Submitted", "webhook_form", {
        source: "WebForm",
        event: "demo.requested",
        howItStarts:
          "Fires when a prospect submits a demo request form on your website, indicating active buying intent for your product or service",
        setup:
          "Configure your demo request form (on /demo or /request-demo page) to POST submissions to this webhook. Include fields for name, email, company, company size, use case description, and preferred demo time.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract demo request details and urgency",
        280,
        "You are a sales operations triage specialist. Parse the demo request form and extract all relevant details. Assess urgency and segment the request for proper routing to the right account executive.",
        "Extract from this demo request submission: {{{1.body}}}. Return JSON with: lead_email, lead_name, company_name, company_size (number of employees), use_case, preferred_time, phone_number, urgency (urgent/standard based on language used), segment (enterprise/mid-market/smb based on company size).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_size", as: "company_size", type: "number" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "segment", as: "segment", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Enrich with existing CRM data", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "existing_contacts", type: "number" },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "existing_owner_id",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Route to territory AE based on company segment", 760, {
        runCodeDetails: {
          code: `const territoryMap = { enterprise: 'ae_enterprise_team', 'mid-market': 'ae_midmarket_team', smb: 'ae_smb_team' };
const assignedTeam = territoryMap[segment] || 'ae_smb_team';
const slaHours = segment === 'enterprise' ? 1 : segment === 'mid-market' ? 4 : 24;
const routingReason = existing_owner_id ? 'existing_owner' : 'territory_' + segment;
return { assigned_team: assignedTeam, sla_hours: slaHours, routing_reason: routingReason };`,
          variables: [
            {
              name: "assigned_team",
              customName: "assigned_team",
              type: "string",
            },
            { name: "sla_hours", customName: "sla_hours", type: "number" },
            {
              name: "routing_reason",
              customName: "routing_reason",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Is this an enterprise demo request?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "segment", operator: "equals", value: "enterprise" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert assigned AE with full demo context",
        1240,
        ":dart: *New Demo Request — {segment}*\n\n*Prospect:* {lead_name} ({lead_email})\n*Company:* {company_name} ({company_size} employees)\n*Use Case:* {use_case}\n*Preferred Time:* {preferred_time}\n*Urgency:* {urgency}\n*SLA:* Respond within {sla_hours}h\n*Assigned Team:* {assigned_team}\n*Routing:* {routing_reason}\n\nPlease claim this demo within the SLA window.",
      ),

      emailNode(
        "7",
        "SendGrid: Confirm demo request to prospect",
        1480,
        "{lead_email}",
        "Demo request received, {lead_name} — we'll be in touch soon!",
        "Hi {lead_name},\n\nThank you for requesting a demo! We're excited to show you how we can help with {use_case}.\n\nHere's what happens next:\n- A dedicated account executive from our {segment} team will reach out within {sla_hours} hours\n- We'll customize the demo specifically for {company_name}\n- The session typically takes 30-45 minutes\n\nIn the meantime, check out our interactive product tour: [link]\n\nLooking forward to connecting!\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log demo request and assignment",
        1720,
        "Demo_Requests",
        `[["{lead_name}","{lead_email}","{company_name}","{segment}","{use_case}","{assigned_team}","{sla_hours}","{urgency}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes demo request form submissions, extracts details and segments via AI, checks HubSpot for existing contacts, routes to the appropriate AE team based on company size territory, alerts the assigned team via Slack with full context and SLA, sends a confirmation email to the prospect, and logs all demo requests to Google Sheets.",
  },

  // ===================================================================
  // 6. Outbound Prospect Enrichment from Domain — Pattern K
  // ===================================================================
  "Outbound Prospect Enrichment from Domain": {
    nodes: [
      trigger("Manual: Outbound Domain Submitted for Enrichment", "manual", {
        howItStarts:
          "Triggered manually when a sales rep submits a target company domain for outbound prospecting enrichment and ICP validation",
        setup:
          "Configure a simple form or Slack command that posts the target domain to this webhook. Include the submitting rep's email and any notes about why this company is being targeted.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Parse domain and prepare enrichment query",
        280,
        "You are an outbound sales research assistant. Parse the submitted domain and prepare structured data for company and contact enrichment. Clean the domain input and identify the target account.",
        "Parse this outbound prospect submission: {{{1.body}}}. Return JSON with: target_domain (cleaned domain like company.com), submitter_email, submitter_notes, company_name_guess (infer from domain), research_priority (high/medium/low).",
        [
          { field: "target_domain", as: "target_domain", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "submitter_notes", as: "submitter_notes", type: "string" },
          {
            field: "company_name_guess",
            as: "company_name_guess",
            type: "string",
          },
          {
            field: "research_priority",
            as: "research_priority",
            type: "string",
          },
        ],
      ),

      n("3", "hitApi", "Clearbit: Enrich company from domain", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={target_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "category.subIndustry",
              customName: "sub_industry",
              type: "string",
            },
            { name: "geo.country", customName: "country", type: "string" },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      n("4", "hitApi", "Apollo: Find key contacts at target company", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "x-api-key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_domains", value: '["{target_domain}"]' },
            { key: "person_seniorities", value: '["director","vp","c_suite"]' },
            { key: "per_page", value: "5" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_1_name",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_1_title",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_1_email",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_contacts_found",
              type: "number",
            },
          ],
        },
      }),

      n("5", "runCode", "Score ICP fit from enrichment data", 1000, {
        runCodeDetails: {
          code: `const industryFit = ['technology','software','saas','fintech','healthcare'].includes(industry?.toLowerCase()) ? 30 : 15;
const sizeFit = employee_count >= 100 && employee_count <= 5000 ? 30 : employee_count > 5000 ? 20 : 10;
const revenueFit = annual_revenue > 5000000 ? 25 : annual_revenue > 1000000 ? 15 : 5;
const contactAvail = total_contacts_found > 0 ? 15 : 0;
const icpScore = industryFit + sizeFit + revenueFit + contactAvail;
return { icp_score: icpScore, icp_match: icpScore >= 70 };`,
          variables: [
            { name: "icp_score", customName: "icp_score", type: "number" },
            { name: "icp_match", customName: "icp_match", type: "boolean" },
          ],
        },
      }),

      n("6", "condition", "Does company match Ideal Customer Profile?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "icp_match", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "7",
        "hitApi",
        "HubSpot: Create company and contacts for ICP match",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/companies",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"domain":"{target_domain}","name":"{company_name}","industry":"{industry}","numberofemployees":"{employee_count}","annualrevenue":"{annual_revenue}","country":"{country}","icp_score":"{icp_score}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_company_id", type: "string" },
            ],
          },
        },
      ),

      slackNode(
        "8",
        "Slack: Send enrichment digest to requesting rep",
        1720,
        ":mag: *Outbound Enrichment Results for {target_domain}*\n\n*Company:* {company_name}\n*Industry:* {industry} / {sub_industry}\n*Employees:* {employee_count} | Revenue: ${annual_revenue}\n*Country:* {country}\n*ICP Score:* {icp_score}/100 ({icp_match})\n\n*Top Contact:* {contact_1_name} — {contact_1_title} ({contact_1_email})\n*Total Contacts Found:* {total_contacts_found}\n\nHubSpot Company: https://app.hubspot.com/companies/{hubspot_company_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Takes a target domain from an outbound rep, enriches company data via Clearbit, finds key decision-maker contacts via Apollo, computes an ICP fit score from industry, size, and revenue signals, creates HubSpot records for ICP matches, and delivers a full enrichment digest back to the requesting rep via Slack.",
  },

  // ===================================================================
  // 7. New Lead Scoring + Routing — Pattern K
  // ===================================================================
  "New Lead Scoring + Routing": {
    nodes: [
      trigger("Web Form: New Lead Captured", "webhook_form", {
        source: "WebForm",
        event: "lead.captured",
        howItStarts:
          "Fires when a new lead is captured through any inbound form on your website, including gated content downloads, newsletter signups, and contact forms",
        setup:
          "Configure all inbound lead capture forms to POST to this webhook URL. Ensure every form passes email, name, company (if available), and the form identifier so the system knows the lead source.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract lead data and normalize fields",
        280,
        "You are a lead data normalization specialist. Parse the incoming form submission, extract all available fields, and normalize them into a standard schema for scoring and routing.",
        "Extract from this lead form submission: {{{1.body}}}. Return JSON with: lead_email, lead_name, company_name, phone, form_source (which form they filled), utm_source, utm_medium, utm_campaign, page_url, has_company (boolean).",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "form_source", as: "form_source", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "has_company", as: "has_company", type: "boolean" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Lookup existing contact and engagement history",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{lead_email}"}]}]',
              },
            ],
            variables: [
              { name: "total", customName: "existing_count", type: "number" },
              {
                name: "results[0].properties.num_conversion_events",
                customName: "prior_conversions",
                type: "number",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "hubspot_contact_id",
                type: "string",
              },
              {
                name: "results[0].properties.hubspot_owner_id",
                customName: "current_owner",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "hitApi", "Clearbit: Enrich company and person data", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={lead_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            {
              name: "person.employment.title",
              customName: "job_title",
              type: "string",
            },
            {
              name: "person.employment.seniority",
              customName: "seniority",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
          ],
        },
      }),

      n("5", "runCode", "Compute behavior + fit score", 1000, {
        runCodeDetails: {
          code: `const fitScore = (employee_count > 200 ? 25 : 10) + (seniority === 'executive' || seniority === 'director' ? 25 : 10) + (annual_revenue > 5000000 ? 20 : 5);
const behaviorScore = (prior_conversions > 2 ? 20 : prior_conversions > 0 ? 10 : 0) + (form_source === 'demo_request' ? 20 : form_source === 'pricing' ? 15 : 5);
const totalScore = fitScore + behaviorScore;
return { lead_score: totalScore, score_label: totalScore > 70 ? 'Hot' : totalScore > 45 ? 'Warm' : 'Cold', routing_queue: totalScore > 70 ? 'immediate_outreach' : 'nurture_queue' };`,
          variables: [
            { name: "lead_score", customName: "lead_score", type: "number" },
            { name: "score_label", customName: "score_label", type: "string" },
            {
              name: "routing_queue",
              customName: "routing_queue",
              type: "string",
            },
          ],
        },
      }),

      n("6", "condition", "Is lead score above hot threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "lead_score", operator: "greaterThan", value: "70" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Hot lead alert to sales team",
        1480,
        ":fire: *HOT LEAD ALERT — Score: {lead_score}*\n\n*Name:* {lead_name} ({lead_email})\n*Title:* {job_title} ({seniority})\n*Company:* {company_name} ({industry})\n*Employees:* {employee_count}\n*Form:* {form_source}\n*UTM:* {utm_source} / {utm_campaign}\n*Prior Conversions:* {prior_conversions}\n*Routing:* {routing_queue}\n\nPlease reach out within 5 minutes for best conversion rates.",
      ),

      n(
        "8",
        "hitApi",
        "HubSpot: Update contact with score and routing assignment",
        1720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"lead_score":"{lead_score}","lead_score_label":"{score_label}","lead_routing_queue":"{routing_queue}","hs_lead_status":"OPEN"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Scores and routes new inbound leads by extracting form data via AI, looking up engagement history in HubSpot, enriching with Clearbit person and company data, computing a composite behavior + fit score, sending instant Slack alerts for hot leads, and updating HubSpot with the score and routing assignment.",
  },

  // ===================================================================
  // 8. MQL to SDR Task Creation — Pattern K
  // ===================================================================
  "MQL to SDR Task Creation": {
    nodes: [
      trigger("HubSpot: Contact Reaches MQL Status", "webhook_app", {
        source: "HubSpot",
        event: "contact.propertyChange",
        howItStarts:
          "Fires when a HubSpot contact's lifecycle stage changes to Marketing Qualified Lead (MQL), indicating they have met your qualification criteria",
        setup:
          "In HubSpot > Settings > Integrations > Webhooks, create a subscription for contact property changes filtered to lifecyclestage = 'marketingqualifiedlead'. Include contact ID, email, and all properties in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract MQL context and recommend outreach approach",
        280,
        "You are a sales development operations assistant. Parse the HubSpot MQL webhook and extract the contacts qualification context. Recommend the best outreach approach based on the leads engagement history and profile.",
        "Extract from this HubSpot MQL webhook: {{{1.body}}}. Return JSON with: contact_id, contact_email, contact_name, company_name, lead_score, mql_reason (what action triggered MQL), recent_activity_summary, recommended_outreach (call/email/linkedin), talking_points (2-3 key points for the SDR to use).",
        [
          { field: "contact_id", as: "contact_id", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "lead_score", as: "lead_score", type: "number" },
          { field: "mql_reason", as: "mql_reason", type: "string" },
          {
            field: "recommended_outreach",
            as: "recommended_outreach",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup associated deals and engagement", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}?associations=deals&properties=email,firstname,lastname,company,jobtitle,lifecyclestage,num_conversion_events,recent_conversion_event_name",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.jobtitle",
              customName: "job_title",
              type: "string",
            },
            {
              name: "properties.num_conversion_events",
              customName: "conversion_count",
              type: "number",
            },
            {
              name: "properties.recent_conversion_event_name",
              customName: "recent_conversion",
              type: "string",
            },
            {
              name: "associations.deals.results[0].id",
              customName: "existing_deal_id",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Build SDR task with outreach instructions", 760, {
        runCodeDetails: {
          code: `const taskTitle = 'Follow up with MQL: ' + contact_name + ' at ' + company_name;
const taskBody = 'MQL Reason: ' + mql_reason + '\\nJob Title: ' + job_title + '\\nConversions: ' + conversion_count + '\\nRecent: ' + recent_conversion + '\\nApproach: ' + recommended_outreach + '\\nTalking Points: ' + talking_points;
const dueDate = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
const priority = lead_score > 80 ? 'HIGH' : 'MEDIUM';
return { task_title: taskTitle, task_body: taskBody, due_date: dueDate, task_priority: priority };`,
          variables: [
            { name: "task_title", customName: "task_title", type: "string" },
            { name: "task_body", customName: "task_body", type: "string" },
            { name: "due_date", customName: "due_date", type: "string" },
            {
              name: "task_priority",
              customName: "task_priority",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Alert SDR with MQL briefing",
        1000,
        ":bell: *New MQL Assigned — {task_priority} Priority*\n\n*Contact:* {contact_name} ({contact_email})\n*Company:* {company_name} | Title: {job_title}\n*Lead Score:* {lead_score}\n*MQL Trigger:* {mql_reason}\n*Conversions:* {conversion_count} (Latest: {recent_conversion})\n*Recommended Approach:* {recommended_outreach}\n*Talking Points:* {talking_points}\n\nTask due by: {due_date}",
      ),

      n("6", "hitApi", "HubSpot: Create SDR follow-up task", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"{task_title}","hs_task_body":"{task_body}","hs_task_priority":"{task_priority}","hs_task_status":"NOT_STARTED","hs_timestamp":"{due_date}"}',
            },
          ],
          variables: [{ name: "id", customName: "task_id", type: "string" }],
        },
      }),

      emailNode(
        "7",
        "SendGrid: SDR briefing email with full context",
        1480,
        "{contact_email}",
        "{contact_name}, thanks for your interest — let's connect!",
        "Hi {contact_name},\n\nI noticed you've been exploring our resources (most recently: {recent_conversion}). As someone working in {job_title} at {company_name}, I think there are a few things that might be particularly relevant to you.\n\nWould you be open to a quick 15-minute call this week? I'd love to learn more about your needs and share how we've helped similar companies.\n\nYou can book time directly here: [calendar link]\n\nBest,\nYour dedicated rep",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log MQL task creation",
        1720,
        "MQL_Tasks",
        `[["{contact_name}","{contact_email}","{company_name}","{lead_score}","{mql_reason}","{recommended_outreach}","{task_priority}","{task_id}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when a HubSpot contact reaches MQL status, extracts qualification context via AI, looks up deals and engagement history, builds a structured SDR task with outreach instructions, alerts the SDR via Slack, creates the follow-up task in HubSpot, sends a personalized outreach email, and logs everything to Google Sheets.",
  },

  // ===================================================================
  // 9. PQL Usage Trigger to Sales Alert — Pattern L
  // ===================================================================
  "PQL Usage Trigger to Sales Alert": {
    nodes: [
      trigger("Product: Usage Milestone Reached", "webhook_app", {
        source: "Product",
        event: "usage.milestone",
        howItStarts:
          "Fires when a user or account hits a product usage milestone that signals Product Qualified Lead status (e.g., activated 3 features, invited team members, exceeded usage threshold)",
        setup:
          "Configure your product analytics platform (Segment, Amplitude, or custom) to fire a webhook when key usage milestones are reached. Include user_id, account_id, milestone_type, and current usage metrics.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract usage metrics and assess expansion signal",
        280,
        "You are a product-led growth analyst. Parse the usage milestone webhook and extract meaningful PQL signals. Determine the strength of the expansion opportunity based on the usage patterns.",
        "Extract from this product usage milestone webhook: {{{1.body}}}. Return JSON with: user_email, user_name, account_id, account_name, milestone_type (feature_adoption/team_growth/usage_limit/engagement_spike), current_plan, usage_percentage (0-100), features_activated (number), team_members (number), days_since_signup.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "account_name", as: "account_name", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "current_plan", as: "current_plan", type: "string" },
          { field: "usage_percentage", as: "usage_percentage", type: "number" },
          {
            field: "features_activated",
            as: "features_activated",
            type: "number",
          },
          { field: "team_members", as: "team_members", type: "number" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup account and deal context", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/companies/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"name","operator":"EQ","value":"{account_name}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_company_id",
              type: "string",
            },
            {
              name: "results[0].properties.annualrevenue",
              customName: "account_arr",
              type: "number",
            },
            {
              name: "results[0].properties.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "account_owner",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Compute PQL expansion score", 760, {
        runCodeDetails: {
          code: `const usageSignal = usage_percentage > 80 ? 35 : usage_percentage > 50 ? 20 : 10;
const adoptionSignal = features_activated > 5 ? 25 : features_activated > 3 ? 15 : 5;
const teamSignal = team_members > 10 ? 20 : team_members > 3 ? 10 : 5;
const planSignal = current_plan === 'free' || current_plan === 'starter' ? 15 : 5;
const expansionScore = usageSignal + adoptionSignal + teamSignal + planSignal;
return { expansion_score: expansionScore, is_pql: expansionScore > 80, expansion_type: usage_percentage > 80 ? 'usage_upgrade' : team_members > 5 ? 'seat_expansion' : 'feature_upgrade' };`,
          variables: [
            {
              name: "expansion_score",
              customName: "expansion_score",
              type: "number",
            },
            { name: "is_pql", customName: "is_pql", type: "boolean" },
            {
              name: "expansion_type",
              customName: "expansion_type",
              type: "string",
            },
          ],
        },
      }),

      n("5", "condition", "Is expansion score above PQL threshold?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "expansion_score", operator: "greaterThan", value: "80" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert AE with PQL expansion opportunity",
        1240,
        ":rocket: *PQL Expansion Signal — Score: {expansion_score}*\n\n*Account:* {account_name} ({industry})\n*User:* {user_name} ({user_email})\n*Current Plan:* {current_plan}\n*Milestone:* {milestone_type}\n*Usage:* {usage_percentage}% | Features: {features_activated} | Team: {team_members}\n*Expansion Type:* {expansion_type}\n*Account ARR:* ${account_arr}\n\nRecommended: Reach out within 24h while usage momentum is high.",
      ),

      n("7", "hitApi", "HubSpot: Create expansion opportunity", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"Expansion: {account_name} — {expansion_type}","pipeline":"default","dealstage":"qualifiedtobuy","hubspot_owner_id":"{account_owner}","expansion_score":"{expansion_score}","expansion_type":"{expansion_type}"}',
            },
          ],
          variables: [{ name: "id", customName: "deal_id", type: "string" }],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log PQL signal and expansion opp",
        1720,
        "PQL_Signals",
        `[["{account_name}","{user_email}","{current_plan}","{milestone_type}","{usage_percentage}","{expansion_score}","{expansion_type}","{deal_id}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects product usage milestones signaling PQL status, extracts usage metrics via AI, looks up account context in HubSpot, computes an expansion score from usage, adoption, and team signals, alerts AEs via Slack for high-score PQLs, creates expansion opportunities in HubSpot, and logs all PQL signals to Google Sheets.",
  },

  // ===================================================================
  // 10. Support Ticket to Expansion Opportunity — Pattern L
  // ===================================================================
  "Support Ticket to Expansion Opportunity": {
    nodes: [
      trigger("Zendesk: Feature Request Ticket Created", "webhook_app", {
        source: "Zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a support ticket tagged as a feature request is created in Zendesk, potentially signaling an expansion or upsell opportunity",
        setup:
          "In Zendesk > Admin > Extensions > Webhooks, create a webhook that triggers on new ticket creation with the tag 'feature_request'. Include ticket subject, description, requester email, organization, and priority.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Classify intent and identify expansion signal",
        280,
        "You are a customer success expansion analyst. Parse the Zendesk feature request ticket and classify the underlying intent. Determine if the request signals a legitimate expansion opportunity or just a feature gap.",
        "Analyze this Zendesk feature request ticket: {{{1.body}}}. Return JSON with: ticket_id, requester_email, requester_name, organization_name, ticket_subject, feature_requested, intent_type (expansion_need/workaround_request/nice_to_have/blocker), expansion_signal_strength (strong/moderate/weak), estimated_impact (high/medium/low), recommended_product (which product tier or add-on addresses this).",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "organization_name",
            as: "organization_name",
            type: "string",
          },
          {
            field: "feature_requested",
            as: "feature_requested",
            type: "string",
          },
          { field: "intent_type", as: "intent_type", type: "string" },
          {
            field: "expansion_signal_strength",
            as: "signal_strength",
            type: "string",
          },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          {
            field: "recommended_product",
            as: "recommended_product",
            type: "string",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup account value and health", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/companies/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"name","operator":"EQ","value":"{organization_name}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.annualrevenue",
              customName: "account_arr",
              type: "number",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_company_id",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "csm_owner_id",
              type: "string",
            },
            {
              name: "results[0].properties.industry",
              customName: "industry",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Score expansion opportunity from signals", 760, {
        runCodeDetails: {
          code: `const signalScore = signal_strength === 'strong' ? 40 : signal_strength === 'moderate' ? 25 : 10;
const impactScore = estimated_impact === 'high' ? 30 : estimated_impact === 'medium' ? 20 : 10;
const valueScore = account_arr > 50000 ? 20 : account_arr > 10000 ? 15 : 5;
const intentBonus = intent_type === 'expansion_need' ? 10 : 0;
const oppScore = signalScore + impactScore + valueScore + intentBonus;
return { opp_score: oppScore, is_high_value: account_arr > 25000, should_create_opp: oppScore > 60 };`,
          variables: [
            { name: "opp_score", customName: "opp_score", type: "number" },
            {
              name: "is_high_value",
              customName: "is_high_value",
              type: "boolean",
            },
            {
              name: "should_create_opp",
              customName: "should_create_opp",
              type: "boolean",
            },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Is this a high-value account with strong signal?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "is_high_value", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Alert CSM with expansion opportunity",
        1240,
        ":bulb: *Expansion Signal from Support Ticket*\n\n*Account:* {organization_name} ({industry}) — ARR: ${account_arr}\n*Requester:* {requester_email}\n*Feature Request:* {feature_requested}\n*Intent:* {intent_type} | Signal: {signal_strength}\n*Recommended Product:* {recommended_product}\n*Opp Score:* {opp_score}\n\nZendesk Ticket: #{ticket_id}\nConsider reaching out to discuss {recommended_product}.",
      ),

      n("7", "hitApi", "HubSpot: Create expansion opportunity deal", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"Expansion: {organization_name} — {recommended_product}","pipeline":"default","dealstage":"appointmentscheduled","hubspot_owner_id":"{csm_owner_id}","deal_source":"support_feature_request","expansion_signal":"{signal_strength}"}',
            },
          ],
          variables: [{ name: "id", customName: "deal_id", type: "string" }],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log expansion signal",
        1720,
        "Expansion_Signals",
        `[["{organization_name}","{requester_email}","{feature_requested}","{intent_type}","{signal_strength}","{opp_score}","{recommended_product}","{account_arr}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures Zendesk feature request tickets, classifies expansion intent and signal strength via AI, looks up account value in HubSpot, scores the expansion opportunity, alerts CSMs via Slack for high-value accounts, creates expansion deals in HubSpot pipeline, and logs all expansion signals to Google Sheets for trend analysis.",
  },

  // ===================================================================
  // 11. Meeting Booked to Account Prep Brief — Pattern K
  // ===================================================================
  "Meeting Booked to Account Prep Brief": {
    nodes: [
      trigger("Calendly: Meeting Booked", "webhook_app", {
        source: "Calendly",
        event: "invitee.created",
        howItStarts:
          "Fires when a prospect books a meeting through Calendly, triggering automated pre-meeting research and account prep for the sales rep",
        setup:
          "In Calendly > Integrations > Webhooks, subscribe to the 'invitee.created' event. Ensure the payload includes invitee name, email, company, meeting type, scheduled time, and any pre-meeting questions answered.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract attendee details from booking",
        280,
        "You are a sales meeting preparation assistant. Parse the Calendly booking webhook and extract structured attendee information to enable comprehensive pre-meeting research.",
        "Extract from this Calendly booking webhook: {{{1.body}}}. Return JSON with: attendee_email, attendee_name, company_name, meeting_type (discovery/demo/follow_up), scheduled_time, meeting_duration_min, questions_answered (summary of any pre-meeting form responses), timezone.",
        [
          { field: "attendee_email", as: "attendee_email", type: "string" },
          { field: "attendee_name", as: "attendee_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
          { field: "scheduled_time", as: "scheduled_time", type: "string" },
          {
            field: "questions_answered",
            as: "questions_answered",
            type: "string",
          },
          { field: "timezone", as: "timezone", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Fetch account history and open deals", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{attendee_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.num_conversion_events",
              customName: "conversion_count",
              type: "number",
            },
            {
              name: "results[0].properties.notes_last_updated",
              customName: "last_note_date",
              type: "string",
            },
          ],
        },
      }),

      n("4", "hitApi", "Clearbit: Enrich company details for prep brief", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "category.industry",
              customName: "industry",
              type: "string",
            },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
            { name: "geo.city", customName: "hq_city", type: "string" },
          ],
        },
      }),

      aiNode(
        "5",
        "OpenAI: Generate pre-meeting account prep brief",
        1000,
        "You are a senior sales strategist. Generate a concise but comprehensive meeting prep brief using the attendee, company, and CRM data provided. Include talking points, potential objections, and discovery questions.",
        "Generate a meeting prep brief for: Attendee: {attendee_name} at {enriched_company} ({industry}). Company: {employee_count} employees, ${annual_revenue} revenue, HQ in {hq_city}. {company_description}. Meeting type: {meeting_type}. Pre-meeting answers: {questions_answered}. CRM stage: {lifecycle_stage}, {conversion_count} prior conversions. Return JSON with: executive_summary, key_talking_points (3 bullet points), discovery_questions (3 questions), potential_objections (2 likely objections with responses), recommended_next_step.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "key_talking_points",
            as: "key_talking_points",
            type: "string",
          },
          {
            field: "discovery_questions",
            as: "discovery_questions",
            type: "string",
          },
          {
            field: "potential_objections",
            as: "potential_objections",
            type: "string",
          },
          {
            field: "recommended_next_step",
            as: "recommended_next_step",
            type: "string",
          },
        ],
      ),

      slackNode(
        "6",
        "Slack: Send prep brief to rep before meeting",
        1240,
        ":clipboard: *Meeting Prep Brief — {attendee_name} @ {enriched_company}*\n\n*Meeting:* {meeting_type} on {scheduled_time} ({timezone})\n*Company:* {enriched_company} | {industry} | {employee_count} emp | ${annual_revenue} rev\n*HQ:* {hq_city}\n\n*Executive Summary:*\n{executive_summary}\n\n*Talking Points:*\n{key_talking_points}\n\n*Discovery Questions:*\n{discovery_questions}\n\n*Likely Objections:*\n{potential_objections}\n\n*Recommended Next Step:* {recommended_next_step}",
      ),

      n("7", "hitApi", "HubSpot: Log meeting activity on contact", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/meetings",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_meeting_title":"{meeting_type} with {attendee_name}","hs_meeting_body":"Prep Brief: {executive_summary}","hs_meeting_start_time":"{scheduled_time}","hs_meeting_outcome":"SCHEDULED"}',
            },
          ],
          variables: [{ name: "id", customName: "meeting_id", type: "string" }],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log meeting booking and prep brief",
        1720,
        "Meeting_Preps",
        `[["{attendee_name}","{attendee_email}","{enriched_company}","{meeting_type}","{scheduled_time}","{industry}","{employee_count}","{recommended_next_step}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered by Calendly bookings, extracts attendee info via AI, fetches CRM history from HubSpot, enriches company data via Clearbit, generates a comprehensive AI-powered prep brief with talking points and objection handling, delivers the brief to the rep via Slack, logs the meeting in HubSpot, and records everything in Google Sheets.",
  },

  // ===================================================================
  // 12. Post-Call Summary to CRM Update — Pattern I
  // ===================================================================
  "Post-Call Summary to CRM Update": {
    nodes: [
      trigger("Gong: Call Recording Completed", "webhook_app", {
        source: "Gong",
        event: "call.completed",
        howItStarts:
          "Fires when a sales call recorded by Gong or Fireflies is completed, with the transcript and metadata available for processing",
        setup:
          "In Gong > Settings > Integrations > Webhooks, configure a webhook for the 'Call Completed' event. Ensure the payload includes call_id, participants, duration, transcript_url, and any auto-detected topics.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Summarize call transcript and extract key themes",
        280,
        "You are a sales call intelligence analyst. Parse the call recording webhook data and transcript to produce a structured summary. Identify key discussion topics, sentiment, and competitive mentions.",
        "Summarize this sales call data: {{{1.body}}}. Return JSON with: call_id, rep_name, prospect_name, prospect_email, prospect_company, call_duration_min, key_topics (comma-separated), overall_sentiment (positive/neutral/negative), competitive_mentions (competitors discussed), pain_points_identified (main challenges the prospect described), call_summary (2-3 sentence summary).",
        [
          { field: "call_id", as: "call_id", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          {
            field: "call_duration_min",
            as: "call_duration_min",
            type: "number",
          },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          {
            field: "pain_points_identified",
            as: "pain_points_identified",
            type: "string",
          },
          { field: "call_summary", as: "call_summary", type: "string" },
        ],
      ),

      aiNode(
        "3",
        "OpenAI: Extract action items and next steps from call",
        520,
        "You are a sales action item extractor. From the call summary and context, identify specific next steps, commitments made, and follow-up tasks that need to be tracked.",
        "Based on this call summary for {prospect_company}: {call_summary}. Pain points: {pain_points_identified}. Sentiment: {overall_sentiment}. Return JSON with: next_steps (array as comma-separated string of specific next actions), follow_up_date_suggestion (relative like 'in 3 days'), deal_stage_recommendation (discovery/qualification/demo/proposal/negotiation), risk_flags (any concerns about deal progression), action_item_count (number of distinct action items).",
        [
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "follow_up_date_suggestion",
            as: "follow_up_date",
            type: "string",
          },
          {
            field: "deal_stage_recommendation",
            as: "deal_stage_rec",
            type: "string",
          },
          { field: "risk_flags", as: "risk_flags", type: "string" },
          {
            field: "action_item_count",
            as: "action_item_count",
            type: "number",
          },
        ],
      ),

      n("4", "hitApi", "HubSpot: Update deal notes with call summary", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"Call Summary ({call_duration_min} min):\\n{call_summary}\\n\\nPain Points: {pain_points_identified}\\nSentiment: {overall_sentiment}\\nNext Steps: {next_steps}\\nRecommended Stage: {deal_stage_rec}\\nRisk Flags: {risk_flags}","hs_timestamp":"' +
                new Date().toISOString() +
                '"}',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      n("5", "condition", "Were specific next steps identified?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "action_item_count", operator: "greaterThan", value: "0" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Notify rep with call summary and action items",
        1240,
        ":phone: *Post-Call Summary — {prospect_company}*\n\n*Rep:* {rep_name} | Duration: {call_duration_min} min\n*Sentiment:* {overall_sentiment}\n*Summary:* {call_summary}\n\n*Pain Points:* {pain_points_identified}\n*Next Steps:* {next_steps}\n*Follow-up by:* {follow_up_date}\n*Deal Stage Rec:* {deal_stage_rec}\n*Risk Flags:* {risk_flags}\n\nGong Call ID: {call_id}",
      ),

      n(
        "7",
        "hitApi",
        "HubSpot: Create follow-up tasks from action items",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_task_subject":"Post-call follow-up: {prospect_company}","hs_task_body":"Action Items from call:\\n{next_steps}\\n\\nDeal Stage Recommendation: {deal_stage_rec}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED"}',
              },
            ],
            variables: [{ name: "id", customName: "task_id", type: "string" }],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log call summary and outcomes",
        1720,
        "Call_Summaries",
        `[["{rep_name}","{prospect_company}","{prospect_email}","{call_duration_min}","{overall_sentiment}","{deal_stage_rec}","{action_item_count}","{risk_flags}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes Gong call recordings, uses AI to summarize the transcript and extract action items and next steps, updates HubSpot deal notes with the call summary, sends the rep a Slack notification with full call intelligence, creates follow-up tasks in HubSpot from extracted action items, and logs call outcomes to Google Sheets.",
  },

  // ===================================================================
  // 13. Lost Deal Reason Capture + Analysis — Pattern L
  // ===================================================================
  "Lost Deal Reason Capture + Analysis": {
    nodes: [
      trigger("HubSpot: Deal Marked as Closed-Lost", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal is moved to Closed-Lost stage, triggering automated loss reason capture, analysis, and team notification",
        setup:
          "In HubSpot > Settings > Integrations > Webhooks, subscribe to deal property changes filtered to dealstage = 'closedlost'. Include deal ID, contact associations, amount, and all deal properties.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract loss details and context",
        280,
        "You are a sales loss analysis specialist. Parse the HubSpot deal closed-lost webhook and extract comprehensive loss context including the stated reason, deal timeline, and contributing factors.",
        "Extract from this HubSpot closed-lost deal webhook: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, contact_email, contact_name, company_name, loss_reason_stated, deal_age_days, deal_stage_when_lost, rep_name, competitor_won (if mentioned), loss_category (price/timing/competitor/fit/champion_left/no_decision).",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "loss_reason_stated",
            as: "loss_reason_stated",
            type: "string",
          },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "loss_category", as: "loss_category", type: "string" },
          { field: "competitor_won", as: "competitor_won", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Fetch deal timeline and contact history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,pipeline,dealstage,closedate,createdate,num_associated_contacts,hs_deal_stage_probability",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            variables: [
              {
                name: "properties.createdate",
                customName: "deal_created",
                type: "string",
              },
              {
                name: "properties.num_associated_contacts",
                customName: "contacts_in_deal",
                type: "number",
              },
              {
                name: "properties.pipeline",
                customName: "pipeline",
                type: "string",
              },
              {
                name: "associations.companies.results[0].id",
                customName: "company_id",
                type: "string",
              },
            ],
          },
        },
      ),

      aiNode(
        "4",
        "OpenAI: Analyze loss pattern and recommend learnings",
        760,
        "You are a sales enablement coach. Analyze the loss details and identify patterns, root causes, and actionable recommendations for the sales team to improve win rates.",
        "Analyze this lost deal: {deal_name} at {company_name} for ${deal_amount}. Loss reason: {loss_reason_stated}. Category: {loss_category}. Competitor: {competitor_won}. Contacts involved: {contacts_in_deal}. Pipeline: {pipeline}. Return JSON with: root_cause_analysis (1-2 sentences), pattern_insight (what pattern this loss fits), improvement_recommendation (specific action for the team), win_back_probability (high/medium/low/none), win_back_strategy (if applicable), coaching_note_for_rep.",
        [
          {
            field: "root_cause_analysis",
            as: "root_cause_analysis",
            type: "string",
          },
          { field: "pattern_insight", as: "pattern_insight", type: "string" },
          {
            field: "improvement_recommendation",
            as: "improvement_recommendation",
            type: "string",
          },
          {
            field: "win_back_probability",
            as: "win_back_probability",
            type: "string",
          },
          {
            field: "win_back_strategy",
            as: "win_back_strategy",
            type: "string",
          },
          {
            field: "coaching_note_for_rep",
            as: "coaching_note_for_rep",
            type: "string",
          },
        ],
      ),

      slackNode(
        "5",
        "Slack: Alert sales manager with loss analysis",
        1000,
        ":x: *Deal Lost — {deal_name}*\n\n*Company:* {company_name} | Amount: ${deal_amount}\n*Rep:* {rep_name}\n*Loss Reason:* {loss_reason_stated}\n*Category:* {loss_category}\n*Competitor:* {competitor_won}\n\n*Root Cause:* {root_cause_analysis}\n*Pattern:* {pattern_insight}\n*Win-Back:* {win_back_probability} — {win_back_strategy}\n\n*Recommendation:* {improvement_recommendation}",
      ),

      n(
        "6",
        "hitApi",
        "HubSpot: Tag deal with loss category and analysis",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"loss_category":"{loss_category}","competitor_lost_to":"{competitor_won}","win_back_probability":"{win_back_probability}","loss_analysis":"{root_cause_analysis}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_deal_id", type: "string" },
            ],
          },
        },
      ),

      emailNode(
        "7",
        "SendGrid: Send loss feedback request to prospect",
        1480,
        "{contact_email}",
        "We'd love your feedback, {company_name}",
        "Hi there,\n\nWe understand you've decided to go in a different direction, and we completely respect that decision.\n\nWe're always working to improve, and your honest feedback would be incredibly valuable to us. Could you share what influenced your decision?\n\nIt would only take 2 minutes: [Feedback Form Link]\n\nRegardless of your answer, we wish you all the best and hope to have the opportunity to work together in the future.\n\nWarm regards,\nThe Sales Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log loss analysis for trend reporting",
        1720,
        "Lost_Deals",
        `[["{deal_name}","{company_name}","{deal_amount}","{rep_name}","{loss_category}","{competitor_won}","{win_back_probability}","{root_cause_analysis}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when a HubSpot deal moves to Closed-Lost, extracts loss details via AI, fetches deal timeline data, analyzes loss patterns and generates coaching recommendations, alerts the sales manager via Slack, tags the deal with structured loss data, sends a feedback request to the prospect, and logs the analysis to Google Sheets for trend reporting.",
  },

  // ===================================================================
  // 14. Deal Stage Change to Internal Handoff — Pattern K
  // ===================================================================
  "Deal Stage Change to Internal Handoff": {
    nodes: [
      trigger("HubSpot: Deal Stage Changed", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal moves to a stage that requires an internal handoff (e.g., from sales to customer success, or from SDR to AE)",
        setup:
          "In HubSpot > Settings > Integrations > Webhooks, subscribe to deal property changes for dealstage. Filter to stages that trigger handoffs (e.g., 'closedwon', 'contractsent', 'onboarding'). Include deal and contact properties.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract deal context and identify handoff type",
        280,
        "You are a sales operations handoff coordinator. Parse the HubSpot deal stage change webhook and determine the appropriate internal handoff requirements based on the new stage, deal size, and complexity.",
        "Extract from this HubSpot deal stage change: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, new_stage, previous_stage, contact_email, contact_name, company_name, current_owner_name, handoff_type (sales_to_cs/sdr_to_ae/ae_to_solutions/cs_to_renewal), deal_complexity (high/medium/low).",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "new_stage", as: "new_stage", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "current_owner_name",
            as: "current_owner_name",
            type: "string",
          },
          { field: "handoff_type", as: "handoff_type", type: "string" },
          { field: "deal_complexity", as: "deal_complexity", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Fetch full contact and company details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=contacts,companies&properties=dealname,amount,pipeline,dealstage,hs_deal_stage_probability,notes_last_updated",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.pipeline",
              customName: "pipeline",
              type: "string",
            },
            {
              name: "properties.notes_last_updated",
              customName: "last_note",
              type: "string",
            },
            {
              name: "associations.contacts.results[0].id",
              customName: "primary_contact_id",
              type: "string",
            },
            {
              name: "associations.companies.results[0].id",
              customName: "company_id",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Determine new owner and handoff requirements", 760, {
        runCodeDetails: {
          code: `const handoffMap = { sales_to_cs: 'cs_team_queue', sdr_to_ae: 'ae_territory_queue', ae_to_solutions: 'solutions_engineering_queue', cs_to_renewal: 'renewal_team_queue' };
const newOwnerQueue = handoffMap[handoff_type] || 'general_queue';
const needsIntroCall = deal_amount > 25000 || deal_complexity === 'high';
const handoffUrgency = deal_complexity === 'high' ? 'urgent' : 'standard';
const briefingRequired = deal_amount > 10000;
return { new_owner_queue: newOwnerQueue, needs_intro_call: needsIntroCall, handoff_urgency: handoffUrgency, briefing_required: briefingRequired };`,
          variables: [
            {
              name: "new_owner_queue",
              customName: "new_owner_queue",
              type: "string",
            },
            {
              name: "needs_intro_call",
              customName: "needs_intro_call",
              type: "boolean",
            },
            {
              name: "handoff_urgency",
              customName: "handoff_urgency",
              type: "string",
            },
            {
              name: "briefing_required",
              customName: "briefing_required",
              type: "boolean",
            },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Does this handoff require CS team involvement?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "handoff_type",
                operator: "equals",
                value: "sales_to_cs",
              },
            ],
          },
        },
      ),

      slackNode(
        "6",
        "Slack: Notify new owner with full handoff context",
        1240,
        ":handshake: *Internal Handoff — {handoff_type}*\n\n*Deal:* {deal_name} | Amount: ${deal_amount}\n*Company:* {company_name}\n*Contact:* {contact_email}\n*From:* {current_owner_name}\n*To Queue:* {new_owner_queue}\n*Stage:* {previous_stage} → {new_stage}\n*Complexity:* {deal_complexity}\n*Urgency:* {handoff_urgency}\n*Intro Call Needed:* {needs_intro_call}\n*Briefing Required:* {briefing_required}\n\nPlease claim this account within 4 hours.",
      ),

      emailNode(
        "7",
        "SendGrid: Send handoff brief to new owner",
        1480,
        "{current_owner_name}@company.com",
        "Handoff Brief: {deal_name} — {company_name}",
        "Hi Team,\n\nA deal handoff is in progress:\n\nDeal: {deal_name}\nAmount: ${deal_amount}\nCompany: {company_name}\nPrimary Contact: {contact_email}\nHandoff Type: {handoff_type}\nComplexity: {deal_complexity}\n\nPlease review the full deal history in HubSpot and schedule an intro call with the customer within 48 hours if applicable.\n\nKey context from the previous stage owner should be reviewed in the deal notes.\n\nBest,\nSales Operations",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log handoff for SLA tracking",
        1720,
        "Handoffs",
        `[["{deal_name}","{company_name}","{deal_amount}","{handoff_type}","{current_owner_name}","{new_owner_queue}","{handoff_urgency}","{deal_complexity}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered by HubSpot deal stage changes that require internal handoffs, extracts deal context via AI, fetches full deal associations, determines the new owner queue and handoff requirements, notifies the receiving team via Slack with complete context, sends a formal handoff brief email, and logs all handoffs to Google Sheets for SLA tracking.",
  },

  // ===================================================================
  // 15. Proposal Requested to Document Generation — Pattern I
  // ===================================================================
  "Proposal Requested to Document Generation": {
    nodes: [
      trigger("HubSpot: Deal Enters Proposal Stage", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal moves to the proposal/presentation stage, triggering automated proposal document generation with deal-specific terms and pricing",
        setup:
          "In HubSpot > Settings > Integrations > Webhooks, subscribe to deal property changes for dealstage = 'presentationscheduled' or a custom 'proposal_requested' stage. Include deal properties including products, pricing, and associated contacts.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract deal terms and proposal requirements",
        280,
        "You are a sales proposal automation specialist. Parse the HubSpot deal stage change webhook and extract all relevant information needed to generate a customized proposal document.",
        "Extract from this HubSpot proposal stage webhook: {{{1.body}}}. Return JSON with: deal_id, deal_name, deal_amount, contact_name, contact_email, company_name, products_requested (comma-separated), contract_length_months, payment_terms, discount_percentage, special_requirements, rep_name.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "products_requested",
            as: "products_requested",
            type: "string",
          },
          {
            field: "discount_percentage",
            as: "discount_percentage",
            type: "number",
          },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          { field: "rep_name", as: "rep_name", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Fetch product line items and pricing", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?associations=line_items&properties=dealname,amount,closedate,deal_currency_code",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.closedate",
              customName: "expected_close",
              type: "string",
            },
            {
              name: "properties.deal_currency_code",
              customName: "currency",
              type: "string",
            },
            {
              name: "associations.line_items.results.length",
              customName: "line_item_count",
              type: "number",
            },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Draft proposal content with pricing breakdown",
        760,
        "You are a professional proposal writer for B2B SaaS sales. Generate polished proposal content including executive summary, solution overview, pricing breakdown, and terms. Make it persuasive and specific to the customer.",
        "Draft a proposal for {company_name}: Products: {products_requested}. Amount: ${deal_amount}. Discount: {discount_percentage}%. Special requirements: {special_requirements}. Expected close: {expected_close}. Line items: {line_item_count}. Return JSON with: executive_summary, solution_overview, pricing_section, terms_conditions, value_proposition, has_custom_pricing (boolean based on discount > 15 or special requirements).",
        [
          { field: "executive_summary", as: "exec_summary", type: "string" },
          {
            field: "solution_overview",
            as: "solution_overview",
            type: "string",
          },
          { field: "pricing_section", as: "pricing_section", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "has_custom_pricing",
            as: "has_custom_pricing",
            type: "boolean",
          },
        ],
      ),

      n(
        "5",
        "condition",
        "Does proposal require custom pricing approval?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "has_custom_pricing",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      n(
        "6",
        "approvalGate",
        "Manager Review: Approve custom pricing terms",
        1240,
        {
          approvalGateDetails: {
            approvers: ["sales_manager"],
            message:
              "Proposal for {company_name} requires approval for custom pricing (${deal_amount}, {discount_percentage}% discount). Special requirements: {special_requirements}",
            timeoutHours: 24,
          },
        },
      ),

      emailNode(
        "7",
        "SendGrid: Deliver proposal to prospect",
        1480,
        "{contact_email}",
        "Your Proposal from {rep_name} — {deal_name}",
        "Dear {contact_name},\n\nThank you for the opportunity to present our proposal for {company_name}.\n\n{exec_summary}\n\nSolution Overview:\n{solution_overview}\n\nPricing:\n{pricing_section}\n\n{value_proposition}\n\nPlease review the attached detailed proposal document. I'm happy to walk you through it at your convenience.\n\nLooking forward to your feedback!\n\nBest regards,\n{rep_name}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log proposal generation",
        1720,
        "Proposals",
        `[["{deal_name}","{company_name}","{deal_amount}","{products_requested}","{discount_percentage}","{has_custom_pricing}","{rep_name}","{expected_close}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Activated when a HubSpot deal enters the proposal stage, extracts deal terms via AI, fetches product line items and pricing, generates polished proposal content with a pricing breakdown, routes custom pricing through a manager approval gate, delivers the proposal to the prospect via email, and logs all proposals to Google Sheets.",
  },

  // ===================================================================
  // 16. Quote Approved to Deal Desk Approval — Pattern K
  // ===================================================================
  "Quote Approved to Deal Desk Approval": {
    nodes: [
      trigger("CRM: Quote Approved by Prospect", "webhook_app", {
        source: "CRM",
        event: "quote.approved",
        howItStarts:
          "Fires when a prospect approves a sales quote, triggering internal deal desk review for discount validation and compliance before contract generation",
        setup:
          "Configure your CPQ or quoting tool to POST a webhook when a quote status changes to 'approved_by_customer'. Include quote ID, deal ID, line items, total amount, discount applied, and payment terms.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract quote details and flag exceptions",
        280,
        "You are a deal desk operations analyst. Parse the approved quote webhook and extract all pricing details. Flag any exceptions that require additional approval such as non-standard discounts, custom terms, or unusually large deals.",
        "Extract from this approved quote webhook: {{{1.body}}}. Return JSON with: quote_id, deal_id, deal_name, company_name, total_amount, discount_applied_pct, payment_terms, contract_length_months, line_items_summary, rep_name, has_non_standard_terms (boolean), exception_flags (comma-separated list of any red flags).",
        [
          { field: "quote_id", as: "quote_id", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "discount_applied_pct", as: "discount_pct", type: "number" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          {
            field: "has_non_standard_terms",
            as: "has_non_standard",
            type: "boolean",
          },
          { field: "exception_flags", as: "exception_flags", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup deal and verify quote alignment", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealname,amount,pipeline,dealstage,hubspot_owner_id",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            {
              name: "properties.amount",
              customName: "deal_amount",
              type: "number",
            },
            {
              name: "properties.hubspot_owner_id",
              customName: "deal_owner_id",
              type: "string",
            },
            {
              name: "properties.pipeline",
              customName: "pipeline",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Check discount limits and approval thresholds", 760, {
        runCodeDetails: {
          code: `const standardDiscountLimit = 20;
const largeTransactionThreshold = 50000;
const overDiscount = discount_pct > standardDiscountLimit;
const largeTransaction = total_amount > largeTransactionThreshold;
const needsDealDesk = overDiscount || largeTransaction || has_non_standard;
const approvalLevel = largeTransaction && overDiscount ? 'vp_approval' : overDiscount ? 'deal_desk_approval' : largeTransaction ? 'manager_approval' : 'auto_approved';
return { needs_deal_desk: needsDealDesk, approval_level: approvalLevel, over_discount: overDiscount, discount_variance: discount_pct - standardDiscountLimit };`,
          variables: [
            {
              name: "needs_deal_desk",
              customName: "needs_deal_desk",
              type: "boolean",
            },
            {
              name: "approval_level",
              customName: "approval_level",
              type: "string",
            },
            {
              name: "over_discount",
              customName: "over_discount",
              type: "boolean",
            },
            {
              name: "discount_variance",
              customName: "discount_variance",
              type: "number",
            },
          ],
        },
      }),

      n(
        "5",
        "condition",
        "Does quote exceed discount or size threshold?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "needs_deal_desk", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n(
        "6",
        "approvalGate",
        "Deal Desk: Review and approve quote terms",
        1240,
        {
          approvalGateDetails: {
            approvers: ["deal_desk"],
            message:
              "Quote #{quote_id} for {company_name} requires {approval_level}. Amount: ${total_amount}, Discount: {discount_pct}% (limit: 20%). Exception flags: {exception_flags}. Payment terms: {payment_terms}.",
            timeoutHours: 48,
          },
        },
      ),

      n("7", "hitApi", "HubSpot: Update deal stage to contract-sent", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealstage":"contractsent","quote_approved":"true","approved_amount":"{total_amount}","approved_discount":"{discount_pct}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_deal_id", type: "string" },
          ],
        },
      }),

      slackNode(
        "8",
        "Slack: Notify rep that quote is approved and deal advancing",
        1720,
        ":white_check_mark: *Quote Approved — Deal Advancing*\n\n*Deal:* {company_name} | Quote #{quote_id}\n*Amount:* ${total_amount} | Discount: {discount_pct}%\n*Approval Level:* {approval_level}\n*Payment Terms:* {payment_terms}\n*Rep:* {rep_name}\n\nDeal has been moved to Contract Sent stage. Please prepare and send the contract.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes customer-approved quotes, extracts pricing details and flags exceptions via AI, verifies deal alignment in HubSpot, checks discount limits and transaction thresholds, routes oversized or non-standard quotes through a deal desk approval gate, updates the deal stage to contract-sent upon approval, and notifies the rep via Slack.",
  },

  // ===================================================================
  // 17. Contract Signed to Onboarding Kickoff — Pattern E
  // ===================================================================
  "Contract Signed to Onboarding Kickoff": {
    nodes: [
      trigger("DocuSign: Contract Fully Executed", "webhook_app", {
        source: "DocuSign",
        event: "envelope.completed",
        howItStarts:
          "Fires when a DocuSign envelope is fully executed (all parties have signed), triggering the transition from sales to customer success and the onboarding kickoff process",
        setup:
          "In DocuSign > Settings > Connect, create a webhook for 'Envelope Completed' events. Include envelope ID, signer details, document name, and any custom fields from the signing experience.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract contract details and customer info",
        280,
        "You are a contract processing specialist. Parse the DocuSign envelope completed webhook and extract key contract details needed to kick off customer onboarding and update the CRM.",
        "Extract from this DocuSign completed envelope webhook: {{{1.body}}}. Return JSON with: envelope_id, document_name, signer_name, signer_email, company_name, contract_value, contract_start_date, contract_end_date, product_purchased, payment_frequency (monthly/annual), special_terms_summary.",
        [
          { field: "envelope_id", as: "envelope_id", type: "string" },
          { field: "signer_email", as: "signer_email", type: "string" },
          { field: "signer_name", as: "signer_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contract_value", as: "contract_value", type: "number" },
          { field: "contract_start_date", as: "start_date", type: "string" },
          {
            field: "product_purchased",
            as: "product_purchased",
            type: "string",
          },
          {
            field: "payment_frequency",
            as: "payment_frequency",
            type: "string",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Update deal to Closed-Won", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"dealname","operator":"CONTAINS_TOKEN","value":"{company_name}"}]}]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.hs_object_id",
              customName: "deal_id",
              type: "string",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "sales_owner_id",
              type: "string",
            },
            {
              name: "results[0].properties.amount",
              customName: "deal_amount",
              type: "number",
            },
          ],
        },
      }),

      n(
        "4",
        "runCode",
        "Create onboarding plan based on product and value",
        760,
        {
          runCodeDetails: {
            code: `const isHighTouch = contract_value > 25000;
const onboardingType = isHighTouch ? 'white_glove' : 'guided_self_serve';
const kickoffDays = isHighTouch ? 3 : 7;
const milestones = isHighTouch
  ? 'Day 1: Kickoff call, Day 7: Config complete, Day 14: Training, Day 30: Health check'
  : 'Day 1: Welcome email, Day 3: Setup guide, Day 7: Check-in, Day 14: Review';
const csAssignment = isHighTouch ? 'senior_csm_queue' : 'csm_pool';
return { onboarding_type: onboardingType, kickoff_deadline_days: kickoffDays, milestones: milestones, cs_assignment: csAssignment };`,
            variables: [
              {
                name: "onboarding_type",
                customName: "onboarding_type",
                type: "string",
              },
              {
                name: "kickoff_deadline_days",
                customName: "kickoff_days",
                type: "number",
              },
              { name: "milestones", customName: "milestones", type: "string" },
              {
                name: "cs_assignment",
                customName: "cs_assignment",
                type: "string",
              },
            ],
          },
        },
      ),

      slackNode(
        "5",
        "Slack: Alert CS team with onboarding kickoff details",
        1000,
        ":tada: *New Customer — Contract Signed!*\n\n*Company:* {company_name}\n*Contact:* {signer_name} ({signer_email})\n*Contract Value:* ${contract_value} ({payment_frequency})\n*Product:* {product_purchased}\n*Start Date:* {start_date}\n\n*Onboarding Plan:*\nType: {onboarding_type}\nKickoff Deadline: {kickoff_days} days\nCS Assignment: {cs_assignment}\n\n*Milestones:*\n{milestones}\n\nPlease claim this customer and schedule the kickoff call.",
      ),

      emailNode(
        "6",
        "SendGrid: Welcome email to new customer",
        1240,
        "{signer_email}",
        "Welcome aboard, {signer_name}! Let's get {company_name} started",
        "Dear {signer_name},\n\nCongratulations on choosing us as your partner! We're thrilled to welcome {company_name} to our family.\n\nHere's what happens next:\n\n1. Your dedicated Customer Success Manager will reach out within {kickoff_days} business days to schedule your kickoff call\n2. We'll set up your {product_purchased} environment and configure it for your team\n3. You'll receive access to our onboarding portal with step-by-step guides\n\nIn the meantime, here are some resources to explore:\n- Quick Start Guide\n- Product Documentation\n- Community Forum\n\nWe're here to ensure your success!\n\nWarmly,\nThe Customer Success Team",
      ),

      n(
        "7",
        "hitApi",
        "HubSpot: Create onboarding tasks and update deal",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_task_subject":"Onboarding Kickoff: {company_name}","hs_task_body":"Contract signed. Product: {product_purchased}. Value: ${contract_value}. Type: {onboarding_type}. Milestones: {milestones}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED"}',
              },
            ],
            variables: [
              { name: "id", customName: "onboarding_task_id", type: "string" },
            ],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log new customer and onboarding plan",
        1720,
        "New_Customers",
        `[["{company_name}","{signer_email}","{contract_value}","{product_purchased}","{start_date}","{onboarding_type}","{cs_assignment}","{milestones}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered when a DocuSign contract is fully signed, extracts contract details via AI, updates the HubSpot deal to Closed-Won, generates an onboarding plan based on contract value and product, alerts the CS team via Slack, sends a welcome email to the new customer, creates onboarding tasks in HubSpot, and logs the new customer to Google Sheets.",
  },

  // ===================================================================
  // 18. Renewal 90-Day Alert + CSM Task — Pattern F
  // ===================================================================
  "Renewal 90-Day Alert + CSM Task": {
    nodes: [
      trigger("Schedule: Daily Renewal Pipeline Scan", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to scan for customer contracts that are within 90 days of renewal, enabling proactive renewal outreach and risk assessment",
        setup:
          "Configure this workflow to run daily at 8 AM. It will query HubSpot for deals with renewal dates in the next 90 days and generate tasks and alerts for the CSM team.",
        inputFields: [],
      }),

      n("2", "hitApi", "HubSpot: Fetch deals renewing within 90 days", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"renewal_date","operator":"LT","value":"' +
                (Date.now() + 90 * 86400000) +
                '"},{"propertyName":"renewal_date","operator":"GT","value":"' +
                Date.now() +
                '"},{"propertyName":"dealstage","operator":"EQ","value":"closedwon"}]}]',
            },
            {
              key: "properties",
              value:
                '["dealname","amount","renewal_date","hubspot_owner_id","associated_company","health_score"]',
            },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "total", customName: "renewals_found", type: "number" },
            {
              name: "results[0].properties.dealname",
              customName: "deal_name",
              type: "string",
            },
            {
              name: "results[0].properties.amount",
              customName: "deal_amount",
              type: "number",
            },
            {
              name: "results[0].properties.renewal_date",
              customName: "renewal_date",
              type: "string",
            },
            {
              name: "results[0].properties.health_score",
              customName: "health_score",
              type: "number",
            },
            {
              name: "results[0].properties.hubspot_owner_id",
              customName: "csm_owner_id",
              type: "string",
            },
          ],
        },
      }),

      n("3", "runCode", "Compute renewal health and risk assessment", 520, {
        runCodeDetails: {
          code: `const daysToRenewal = Math.ceil((new Date(renewal_date).getTime() - Date.now()) / 86400000);
const healthRisk = health_score < 50 ? 'at_risk' : health_score < 75 ? 'monitor' : 'healthy';
const urgencyLevel = daysToRenewal <= 30 ? 'critical' : daysToRenewal <= 60 ? 'high' : 'standard';
const recommendedAction = healthRisk === 'at_risk' ? 'executive_sponsor_call' : daysToRenewal <= 30 ? 'renewal_proposal' : 'health_check_meeting';
return { days_to_renewal: daysToRenewal, health_risk: healthRisk, urgency_level: urgencyLevel, recommended_action: recommendedAction };`,
          variables: [
            {
              name: "days_to_renewal",
              customName: "days_to_renewal",
              type: "number",
            },
            { name: "health_risk", customName: "health_risk", type: "string" },
            {
              name: "urgency_level",
              customName: "urgency_level",
              type: "string",
            },
            {
              name: "recommended_action",
              customName: "recommended_action",
              type: "string",
            },
          ],
        },
      }),

      n("4", "condition", "Is this renewal at risk?", 760, {
        conditionDetails: {
          conditions: [
            { field: "health_risk", operator: "equals", value: "at_risk" },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Alert CSM with renewal risk assessment",
        1000,
        ":warning: *Renewal Alert — {urgency_level}*\n\n*Account:* {deal_name}\n*ARR:* ${deal_amount}\n*Renewal Date:* {renewal_date} ({days_to_renewal} days away)\n*Health Score:* {health_score}/100\n*Risk Level:* {health_risk}\n*Urgency:* {urgency_level}\n\n*Recommended Action:* {recommended_action}\n\nTotal renewals in pipeline: {renewals_found}",
      ),

      n("6", "hitApi", "HubSpot: Create renewal task for CSM", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"Renewal: {deal_name} — {days_to_renewal} days","hs_task_body":"Health: {health_risk} ({health_score}/100). Recommended: {recommended_action}. ARR: ${deal_amount}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hubspot_owner_id":"{csm_owner_id}"}',
            },
          ],
          variables: [{ name: "id", customName: "task_id", type: "string" }],
        },
      }),

      emailNode(
        "7",
        "SendGrid: Send renewal prep brief to CSM",
        1480,
        "csm-team@company.com",
        "Renewal Prep: {deal_name} — {days_to_renewal} days remaining",
        "Hi CSM Team,\n\nRenewal Alert for {deal_name}:\n\nARR: ${deal_amount}\nRenewal Date: {renewal_date} ({days_to_renewal} days)\nHealth Score: {health_score}/100\nRisk Assessment: {health_risk}\n\nRecommended Action: {recommended_action}\n\nPlease review the account health dashboard and prepare your renewal strategy. If the account is at risk, schedule an executive sponsor call within the next 5 business days.\n\nTotal renewals in 90-day pipeline: {renewals_found}\n\nBest,\nRevOps Automation",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log renewal pipeline status",
        1720,
        "Renewal_Pipeline",
        `[["{deal_name}","{deal_amount}","{renewal_date}","{days_to_renewal}","{health_score}","{health_risk}","{urgency_level}","{recommended_action}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to scan for upcoming renewals within 90 days, fetches renewal deals from HubSpot, computes health risk and urgency levels, alerts CSMs via Slack for at-risk accounts, creates renewal tasks in HubSpot with recommended actions, sends prep briefs via email, and logs the full renewal pipeline status to Google Sheets.",
  },
};
